<script>
	import { onMount } from "svelte";
	import { navigate } from "svelte-routing";
	import { Row, Col } from "@sveltestrap/sveltestrap";
	import CustomSearch from "../../../../components/CustomSearch/CustomSearch.svelte";
	import CustomTextArea from "../../../../components/CustomTextArea/CustomTextArea.svelte";
	import CustomFileInput from "../../../../components/CustomFileInput/CustomFileInput.svelte";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import Loader from "../../../../components/Loader/Loader.svelte";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import { uploadIssueMedia } from "src/services/maintenanceRepair/mediaUpload";
	import UploadedMediaList from "./UploadedMediaList.svelte";
	import DiscardChangesModal from "./DiscardChangesModal.svelte";
	import Lightbox from "../../../../components/Lightbox/Lightbox.svelte";
	import {
		errorMessage,
		imageLightboxIndex,
		isLightBoxOpen,
		cachedServiceCategories,
		toastMessage,
	} from "src/store/store";
	import {
		canEditIssue,
		formatErrorMessage,
		isVideoFile,
	} from "src/utils/misc";
	import {
		getIssueById,
		getIssueTypes,
		createVehicleIssue,
		updateVehicleIssue,
	} from "src/services/maintenanceRepair";
	import { serviceCategoriesToOptions } from "src/utils/options";
	import { fetchServiceCategories } from "src/services/lookupCache";
	import { DEFAULT_TAB_VALUE, vehicleTabPath } from "../../Tabs/tabRoutes";

	export let vehicleId = "";
	export let onDirtyChange = () => {};
	export let issueId = null;
	export let onIssueLoaded = () => {};

	$: isEdit = !!issueId;

	let categoriesError = false;
	$: serviceCategoryOptions = serviceCategoriesToOptions(
		$cachedServiceCategories
	);

	let serviceCategory = "";
	let issueType = "";
	let issueTypeOptions = [];
	let issueTypesLoaded = false;
	let pendingIssueTypeId = null;
	let loadedIssue = null;
	let isLoadingIssue = false;
	let isPrefilled = false;
	let typeUnavailable = false;
	$: isFormLoading = isEdit && !isPrefilled && !err;
	let initialMediaSignature = "";
	let isConfirmOpen = false;

	async function loadIssueTypes(code) {
		issueType = "";
		issueTypeOptions = [];
		issueTypesLoaded = false;
		typeUnavailable = false;
		if (!code) return;
		const options = await getIssueTypes(code);
		if (code !== categoryCode) return;
		issueTypeOptions = options.map(({ id, label }) => ({
			value: String(id),
			label,
			id,
		}));
		issueTypesLoaded = true;
		if (pendingIssueTypeId !== null) {
			issueType =
				issueTypeOptions.find(({ id }) => id === pendingIssueTypeId) ||
				"";
			typeUnavailable = !issueType;
			pendingIssueTypeId = null;
			isPrefilled = true;
		}
	}

	const sameCategory = (code = "", wire = "") =>
		code === wire || code.replace("Non-workshop", "NonWorkshop") === wire;

	async function loadIssue() {
		isLoadingIssue = true;
		try {
			const [res] = await Promise.all([
				getIssueById(issueId),
				fetchServiceCategories().catch(() => (categoriesError = true)),
			]);
			const issue = res.data;
			loadedIssue = issue;
			onIssueLoaded(issue);
			if (!canEditIssue(issue)) {
				err = "This issue can't be edited.";
				return;
			}
			const media = [...(issue.media ?? [])].sort(
				(a, b) => a.sortOrder - b.sortOrder
			);
			files = media.map((item) => ({
				id: nextId++,
				mediaId: item.id,
				url: item.s3Url,
				mediaType: item.mediaType,
				canDelete: true,
				status: "done",
			}));
			initialMediaSignature = media.map(({ id }) => id).join("|");
			issueDescription = issue.issueDescription ?? "";
			zendeskTicket = issue.zendeskId ?? "";
			relatedCase = issue.relatedCaseNo ?? "";
			pendingIssueTypeId = issue.issueType?.id ?? null;
			serviceCategory =
				serviceCategoriesToOptions($cachedServiceCategories).find(
					({ value }) => sameCategory(value, issue.serviceCategory)
				) || "";
			if (!serviceCategory || pendingIssueTypeId === null) {
				pendingIssueTypeId = null;
				isPrefilled = true;
			}
		} catch (error) {
			err = formatErrorMessage(error);
		} finally {
			isLoadingIssue = false;
		}
	}
	$: categoryCode = serviceCategory?.value;
	$: loadIssueTypes(categoryCode);
	let issueDescription = "";
	let zendeskTicket = "";
	let relatedCase = "";
	let files = [];
	let err = "";
	let nextId = 0;

	function updateFile(id, patch) {
		files = files.map((f) => (f.id === id ? { ...f, ...patch } : f));
	}

	let uploadQueue = Promise.resolve();

	function enqueueUpload(id, file) {
		updateFile(id, { status: "uploading", progress: 0 });
		uploadQueue = uploadQueue.then(() => uploadFile(id, file));
	}

	async function uploadFile(id, file) {
		if (!files.some((f) => f.id === id)) return;
		try {
			const url = await uploadIssueMedia(file, (progress) =>
				updateFile(id, { progress })
			);
			updateFile(id, { status: "done", url });
		} catch (error) {
			err = formatErrorMessage(error);
			updateFile(id, { status: "error" });
		}
	}

	function handleFileChange(e) {
		err = "";
		const newFiles = Array.from(e.target.files).map((file) => ({
			id: nextId++,
			file,
			canDelete: true,
			status: "uploading",
			progress: 0,
		}));
		files = [...files, ...newFiles];
		newFiles.forEach(({ id, file }) => enqueueUpload(id, file));
	}

	function handleRemoveFile(e) {
		err = "";
		files = files.filter((_, index) => index !== e.detail.index);
	}

	function handleRetry(e) {
		err = "";
		const { id, file } = files[e.detail.index];
		enqueueUpload(id, file);
	}

	$: isUploading = files.some((f) => f.status === "uploading");
	$: hasFailedUpload = files.some((f) => f.status === "error");

	$: isDirty = isEdit
		? isPrefilled &&
			(!sameCategory(
				serviceCategory?.value,
				loadedIssue.serviceCategory
			) ||
				(typeUnavailable && !issueType
					? false
					: issueType?.id !== loadedIssue.issueType?.id) ||
				issueDescription.trim() !==
					(loadedIssue.issueDescription ?? "").trim() ||
				zendeskTicket.trim() !== (loadedIssue.zendeskId ?? "") ||
				relatedCase.trim() !== (loadedIssue.relatedCaseNo ?? "") ||
				files.map((f) => f.mediaId ?? f.url).join("|") !==
					initialMediaSignature)
		: !!serviceCategory ||
			!!issueType ||
			!!issueDescription.trim() ||
			!!zendeskTicket.trim() ||
			!!relatedCase.trim() ||
			files.length > 0;
	$: onDirtyChange(isDirty);

	$: isCreateDisabled =
		!serviceCategory ||
		!issueType ||
		!issueDescription.trim() ||
		isUploading ||
		hasFailedUpload ||
		isSubmitting ||
		(isEdit && (!isDirty || isLoadingIssue || !canEditIssue(loadedIssue)));

	function goBack() {
		const returnTo = new URLSearchParams(window.location.search).get(
			"returnTo"
		);
		const isAppPath =
			returnTo?.startsWith("/") && !returnTo.startsWith("//");
		navigate(
			isAppPath ? returnTo : vehicleTabPath(vehicleId, DEFAULT_TAB_VALUE)
		);
	}

	let isSubmitting = false;

	const buildPayload = () => ({
		serviceCategory: serviceCategory.value,
		issueTypeId: issueType.id,
		issueDescription: issueDescription.trim(),
		zendeskId: zendeskTicket.trim().replace(/^.*\//, "") || undefined,
		relatedCaseNo: relatedCase.trim() || undefined,
		media: files
			.filter((f) => f.status === "done")
			.map((f, sortOrder) =>
				f.mediaId === undefined
					? {
							s3Url: f.url,
							mediaType: isVideoFile(f.file) ? "video" : "photo",
							sortOrder,
						}
					: { id: f.mediaId, sortOrder }
			),
	});

	async function handleSubmit(request, successMessage) {
		err = "";
		isSubmitting = true;
		try {
			await request();
			toastMessage.set({ type: "success", message: successMessage });
			goBack();
		} catch (error) {
			err = formatErrorMessage(error);
		} finally {
			isSubmitting = false;
			isConfirmOpen = false;
		}
	}

	const handleCreate = () =>
		handleSubmit(
			() => createVehicleIssue(vehicleId, buildPayload()),
			"Issue created"
		);

	const handleUpdate = () =>
		handleSubmit(
			() => updateVehicleIssue(issueId, buildPayload()),
			"Changes saved"
		);

	onMount(() => {
		if (issueId) {
			loadIssue();
			return;
		}
		fetchServiceCategories().catch(() => (categoriesError = true));
	});
</script>

{#if $isLightBoxOpen}
	<Lightbox
		on:click={() => isLightBoxOpen.set(false)}
		imageShowingIndex={$imageLightboxIndex}
		videoFrameThumbnails
	/>
{/if}

<Row>
	<Col xs={12}>
		<ErrorMessageHandler message={err || $errorMessage} />

		{#if isFormLoading}
			<Loader />
		{/if}

		<div
			class="create-issue-form fds-dropdown-list"
			class:d-none={isFormLoading}
		>
			<h5 class="fw-bold mb-0">
				{isEdit ? "Edit issue" : "Create issue"}
			</h5>
			<div class="form-row">
				<div class="form-field">
					<div class="form-field-label">
						Service category<span class="required-marker">*</span>
					</div>
					<CustomSearch
						label="Service category"
						showSearchIcon
						chevron
						placeholder={categoriesError
							? "Failed to load categories"
							: $cachedServiceCategories?.length === 0
								? "No categories available"
								: "Select a category"}
						bind:value={serviceCategory}
						items={serviceCategoryOptions}
					/>
				</div>
				<div class="form-field">
					<div class="form-field-label">
						Issue type<span class="required-marker">*</span>
					</div>
					<CustomSearch
						label="Issue type"
						showSearchIcon
						chevron
						placeholder={issueTypesLoaded &&
						!issueTypeOptions.length
							? "No issue types available"
							: "Select a type"}
						bind:value={issueType}
						items={issueTypeOptions}
						disabled={!serviceCategory}
					/>
					{#if typeUnavailable && !issueType}
						<small class="error-text">
							This issue's type is no longer available. Select a
							new type to continue.
						</small>
					{/if}
				</div>
			</div>

			<div class="form-field">
				<label for="ci-description" class="form-field-label">
					Issue description<span class="required-marker">*</span>
				</label>
				<CustomTextArea
					id="ci-description"
					placeholder="Describe the issue in further detail"
					maxlength={500}
					bind:value={issueDescription}
				/>
			</div>

			<div class="form-row">
				<div class="form-field">
					<label for="ci-zendesk" class="form-field-label">
						Zendesk ticket
					</label>
					<div class="zendesk-input">
						<span class="zendesk-prefix">zendesk.com/</span>
						<input
							id="ci-zendesk"
							type="text"
							class="form-control getgo input"
							placeholder="e.g. 123124"
							bind:value={zendeskTicket}
						/>
					</div>
				</div>
				<div class="form-field">
					<label for="ci-related-case" class="form-field-label">
						Related case
					</label>
					<input
						id="ci-related-case"
						type="text"
						class="form-control getgo input search-icon"
						placeholder="e.g. CASE20260722_G39MB4"
						bind:value={relatedCase}
					/>
				</div>
			</div>

			<div class="form-field">
				<div class="form-field-label">Photos/videos of issue</div>
				<UploadedMediaList
					{files}
					on:remove={handleRemoveFile}
					on:retry={handleRetry}
				/>
				{#if hasFailedUpload}
					<small class="error-text">
						Some files failed to upload. Retry or remove them to
						continue.
					</small>
				{/if}
				<CustomFileInput
					title=""
					fileType="media"
					maxFileSize={1024 * 1024 * 1024}
					{handleFileChange}
				/>
			</div>

			<div class="form-actions">
				<CustomButton
					buttonText="Cancel"
					color="secondary"
					disabled={isSubmitting}
					on:click={goBack}
				/>
				<CustomButton
					buttonText={isEdit ? "Save changes" : "Create issue"}
					disabled={isCreateDisabled}
					on:click={isEdit
						? () => (isConfirmOpen = true)
						: handleCreate}
				/>
			</div>
		</div>
	</Col>
</Row>

{#if isConfirmOpen}
	<DiscardChangesModal
		isOpen
		title="Save changes made?"
		text="You’ll still be able to edit this if needed."
		confirmText="Save changes"
		disabled={isSubmitting}
		on:cancel={() => (isConfirmOpen = false)}
		on:confirm={handleUpdate}
	/>
{/if}

<style>
	.create-issue-form {
		display: flex;
		flex-direction: column;
		gap: 24px;
		max-width: 712px;
	}

	.form-row {
		display: flex;
		gap: 24px;
	}

	.form-field {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.form-field-label {
		font-size: 16px;
		color: var(--fds-on-surface, #00071f);
	}

	.required-marker {
		color: var(--fds-error, #d60c0c);
	}

	.zendesk-input {
		position: relative;
	}

	.zendesk-prefix {
		pointer-events: none;
		position: absolute;
		left: 16px;
		top: 50%;
		transform: translateY(-50%);
		color: var(--fds-on-surface-variant);
	}

	.zendesk-input input {
		padding-left: 124px;
	}

	.form-actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}

	.form-actions :global(.fds-button.btn-secondary),
	.form-actions :global(.fds-button.btn-primary) {
		border: 1px solid #d3d3d3;
	}

	.form-actions :global(.fds-button.btn-primary:not(:disabled)) {
		background-color: var(--fds-primary, #003bed);
		color: var(--fds-on-primary, #fafbff);
	}
</style>
